import { Controller } from "@hotwired/stimulus"

// Phase 2.6 Tier 2 — Risk Links panel controller. Owns the
// "+ Add Link" modal, the per-type search picker, and DOM updates on
// add / remove. Backed by RiskLinksController.
export default class extends Controller {
  static targets = [
    "modal", "typeSelect", "kindSelect", "search", "searchResults",
    "notes", "errorBox", "submitBtn",
    "sections", "rowGroup", "row", "emptyState"
  ]

  static values = {
    createUrl: String,
    pickerUrl: String
  }

  connect() {
    this._selected = null
  }

  // ─── Modal management ────────────────────────────────────────────

  openModal() {
    this.modalTarget.classList.remove("hidden")
    this._reset()
    this._search()
  }

  hideModal() {
    this.modalTarget.classList.add("hidden")
  }

  hideModalOnBackdrop(event) {
    if (event.target === this.modalTarget) this.hideModal()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  typeChanged() {
    this._selected = null
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this._search()
  }

  search() {
    clearTimeout(this._searchTimer)
    this._searchTimer = setTimeout(() => this._search(), 200)
  }

  async _search() {
    if (!this.hasSearchResultsTarget || !this.hasTypeSelectTarget) return
    const type = this.typeSelectTarget.value
    const q = this.hasSearchTarget ? this.searchTarget.value.trim() : ""
    const url = `${this.pickerUrlValue}?type=${encodeURIComponent(type)}&q=${encodeURIComponent(q)}`
    try {
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      const json = await res.json()
      this._renderSearchResults(json.rows || [])
    } catch (e) {
      this._renderSearchResults([])
    }
  }

  _renderSearchResults(rows) {
    if (!rows || rows.length === 0) {
      this.searchResultsTarget.innerHTML = `
        <div class="px-3 py-4 text-center text-[12px] text-gray-500">No matching records.</div>`
      return
    }
    this.searchResultsTarget.innerHTML = rows.map(r => `
      <button type="button" class="w-full text-left px-3 py-2 hover:bg-gray-50 cursor-pointer"
              data-action="click->risk-link#selectTarget"
              data-linkable-id="${r.id}">
        <div class="text-[13px] text-gray-800">${this._esc(r.label)}</div>
        ${r.sub ? `<div class="text-[11px] text-gray-500 mt-0.5">${this._esc(r.sub)}</div>` : ""}
      </button>`).join("")
  }

  selectTarget(event) {
    this._selected = event.currentTarget.dataset.linkableId
    this.searchResultsTarget.querySelectorAll("button").forEach(b => {
      b.classList.toggle("bg-brand/5", b.dataset.linkableId === this._selected)
      b.classList.toggle("ring-1", b.dataset.linkableId === this._selected)
      b.classList.toggle("ring-brand", b.dataset.linkableId === this._selected)
    })
  }

  async submit() {
    this._clearError()
    if (!this._selected) {
      this._showError("Pick a record to link.")
      return
    }
    const fd = new FormData()
    fd.append("linkable_type", this.typeSelectTarget.value)
    fd.append("linkable_id", this._selected)
    fd.append("link_kind", this.kindSelectTarget.value)
    if (this.hasNotesTarget) fd.append("notes", this.notesTarget.value)

    this.submitBtnTarget.disabled = true
    try {
      const json = await this._post(this.createUrlValue, fd)
      if (!json.ok) {
        this._showError((json.errors || ["Save failed"]).join(", "))
        return
      }
      this._appendLink(json.link)
      this._showToast("Link added", "notice")
      this.hideModal()
    } finally {
      this.submitBtnTarget.disabled = false
    }
  }

  async remove(event) {
    const id = event.currentTarget.dataset.linkId
    const row = this._rowFor(id)
    if (!row) return
    const json = await this._delete(row.dataset.destroyUrl)
    if (json.ok) {
      const group = row.parentElement
      row.remove()
      // Drop the section header if its group is empty.
      if (group && group.querySelectorAll("li").length === 0) {
        group.parentElement?.remove()
      }
      this._refreshEmptyState()
      this._showToast("Link removed", "notice")
    } else {
      this._showToast("Remove failed", "alert")
    }
  }

  // ─── DOM helpers ─────────────────────────────────────────────────

  _appendLink(link) {
    const sections = this.sectionsTarget
    sections.classList.remove("hidden")
    let section = sections.querySelector(`[data-link-type="${link.linkable_type}"]`)
    if (!section) {
      section = document.createElement("div")
      section.dataset.linkType = link.linkable_type
      section.innerHTML = `
        <div class="flex items-center gap-2 mb-1">
          <span class="text-[11px] uppercase tracking-wide text-gray-500">${this._typeLabel(link.linkable_type)}</span>
          <span class="text-[11px] text-gray-400" data-link-count>0</span>
        </div>
        <ul class="rounded-lg border border-gray-200 divide-y divide-gray-100"
            data-risk-link-target="rowGroup"></ul>`
      sections.appendChild(section)
    }
    const ul = section.querySelector("ul")
    const li = document.createElement("li")
    li.dataset.linkId = link.id
    li.dataset.riskLinkTarget = "row"
    li.dataset.destroyUrl = `${this.createUrlValue}/${link.id}`
    li.className = "flex items-start gap-3 px-3 py-2 hover:bg-gray-50"
    li.innerHTML = `
      <span class="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand/10 text-brand mt-0.5">·</span>
      <div class="flex-1 min-w-0">
        <div class="text-[13px] text-gray-800 truncate">
          ${link.url
            ? `<a href="${link.url}" target="_blank" class="hover:underline">${this._esc(link.summary)}</a>`
            : this._esc(link.summary)}
        </div>
        <div class="text-[11px] text-gray-500 mt-0.5">
          <span class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] uppercase tracking-wide">
            ${this._esc(link.link_kind)}
          </span>
        </div>
      </div>
      <button type="button" data-action="click->risk-link#remove"
              data-link-id="${link.id}"
              class="shrink-0 px-2 py-0.5 text-[12px] text-rose-700 hover:bg-rose-50 rounded cursor-pointer">
        Remove
      </button>`
    ul.appendChild(li)
    const counter = section.querySelector("[data-link-count]")
    if (counter) counter.textContent = ul.querySelectorAll("li").length
    this._refreshEmptyState()
  }

  _refreshEmptyState() {
    if (!this.hasEmptyStateTarget) return
    const any = this.sectionsTarget.querySelectorAll("[data-risk-link-target='row']").length > 0
    this.emptyStateTarget.classList.toggle("hidden", any)
    this.sectionsTarget.classList.toggle("hidden", !any)
  }

  _rowFor(id) {
    return this.rowTargets.find(r => r.dataset.linkId === id) || null
  }

  _typeLabel(t) {
    return ({
      BudgetLineItem: "Budget Lines", Task: "Schedule Tasks",
      Punchlist: "Action Items", PurchaseOrder: "Purchase Orders",
      ChangeOrderFinal: "Change Orders", FundAllocation: "Fund Allocations",
      CapitalRequest: "Capital Requests", EvaSnapshot: "EVA Snapshots",
      CashFlowForecast: "Cash Flow Forecasts", Asset: "Assets"
    })[t] || t
  }

  _reset() {
    this._selected = null
    if (this.hasSearchTarget) this.searchTarget.value = ""
    if (this.hasNotesTarget) this.notesTarget.value = ""
    if (this.hasSearchResultsTarget) this.searchResultsTarget.innerHTML = ""
    this._clearError()
  }

  _showError(msg) {
    if (!this.hasErrorBoxTarget) return
    this.errorBoxTarget.textContent = msg
    this.errorBoxTarget.classList.remove("hidden")
  }

  _clearError() {
    if (!this.hasErrorBoxTarget) return
    this.errorBoxTarget.classList.add("hidden")
    this.errorBoxTarget.textContent = ""
  }

  _showToast(message, type) {
    const toast = document.createElement("div")
    toast.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.dataset.controller = "flash"
    toast.dataset.flashDismissAfterValue = "5000"
    toast.textContent = message
    document.body.prepend(toast)
  }

  // ─── Network helpers ─────────────────────────────────────────────

  async _post(url, fd) {
    const res = await fetch(url, {
      method: "POST", body: fd,
      headers: { "X-CSRF-Token": csrfToken(), "Accept": "application/json" }
    })
    return res.json()
  }

  async _delete(url) {
    const res = await fetch(url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken(), "Accept": "application/json" }
    })
    return res.json()
  }

  _esc(s) {
    if (s == null) return ""
    return String(s).replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.content : ""
}
